import { type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { Tabs as AntdTabs, Flex, theme } from "antd";
import { createStyles } from "antd-style";

import { Input } from "@av/components/atoms/input/Input";
import { Tag } from "@av/components/atoms/tag/Tag";
import { Typography } from "@av/components/atoms/typography/Typography";
import { SecurityGroupUsersTable } from "./SecurityGroupUsersTable";
import { filterSecurityGroupMembers } from "../../../../hooks/filter-admin-settings-users";

import type {
  HrAdminSettingsUser,
  SecurityGroupSummary,
} from "../../../../../services/admin-settings/type";

const useStyles = createStyles(({ css, token }) => ({
  pane: css`
    flex: 1;
    min-width: 0;
  `,
  name: css`
    font-size: ${token.fontSizeHeading4}px;
    line-height: ${token.lineHeightHeading4};
  `,
  header: css`
    gap: ${token.marginXXS}px;
  `,
  helper: css`
    font-size: ${token.fontSizeSM}px;
    color: ${token.colorTextSecondary};
  `,
}));

type SecurityGroupDetailProps = {
  group: SecurityGroupSummary;
  users?: HrAdminSettingsUser[];
  isLoading: boolean;
  rolesAndPermissionsContent?: ReactNode;
};

export function SecurityGroupDetail({
  group,
  users,
  isLoading,
  rolesAndPermissionsContent,
}: SecurityGroupDetailProps) {
  const { t } = useTranslation();
  const { styles } = useStyles();
  const { token } = theme.useToken();
  const [searchText, setSearchText] = useState("");
  const hasRolesContent = rolesAndPermissionsContent != null;
  const filteredUsers = filterSecurityGroupMembers({ users: users ?? [], searchText });

  return (
    <Flex
      vertical
      gap="middle"
      className={styles.pane}
      style={{
        paddingBlock: token.paddingXS,
        paddingInlineStart: token.paddingLG,
        paddingInlineEnd: token.paddingLG + token.padding,
      }}
    >
      <Flex vertical className={styles.header}>
        <Flex align="center" gap="small">
          <Typography.Text strong className={styles.name} testIdPrefix="security-group-name">
            {group.name}
          </Typography.Text>
          <Tag>{t("platform.adminSettings.products_hr")}</Tag>
        </Flex>
        <Typography.Text className={styles.helper} testIdPrefix="security-group-helper">
          {t("platform.adminSettings.securityGroups_helper")}
        </Typography.Text>
      </Flex>
      <AntdTabs
        defaultActiveKey={hasRolesContent ? "rolesAndPermissions" : "users"}
        items={[
          {
            key: "rolesAndPermissions",
            label: t("platform.adminSettings.rolesAndPermissions"),
            disabled: !hasRolesContent,
            children: rolesAndPermissionsContent,
          },
          {
            key: "users",
            label: t("platform.adminSettings.securityGroups_usersTab", {
              count: users?.length ?? group.userCount,
            }),
            children: (
              <Flex vertical gap="middle">
                <Input.Search
                  aria-label={t("platform.adminSettings.searchUsers")}
                  placeholder={t("platform.adminSettings.securityGroups_searchUsers")}
                  value={searchText}
                  onChange={(event) => setSearchText(event.target.value)}
                  onKeyDown={(event) => event.key === "Escape" && setSearchText("")}
                  testIdPrefix="security-group-users-search"
                />
                <SecurityGroupUsersTable users={filteredUsers} isLoading={isLoading} />
              </Flex>
            ),
          },
        ]}
      />
    </Flex>
  );
}
